(() => {
  'use strict';
  const versionKey = '_mp_properties_version';
  const recordKey = '_mp_properties';
  const fields = [
    ['Piece', 'Modèle'], ['Metal', 'Métal'], ['Karat', 'Pureté de l’or'],
    ['Lettering', 'Lettrage'], ['Name', 'Prénom'], ['Letter count', 'Nombre de caractères'],
    ['Engraving', 'Gravure'], ['Coin diameter', 'Diamètre de la médaille'],
    ['Personalized order acknowledgement', 'Conditions de personnalisation acceptées'],
    ['Acknowledgement text', 'Texte de confirmation'], ['Ring size', 'Taille de bague']
  ];
  const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
  const fail = message => { throw new Error('Invalid personalization properties: ' + message); };
  const labels = new Map(fields.flatMap(([key, label]) => [[key, key], [label, key]]));
  const materialAliases = new Map([
    ['Yellow Gold', 'Yellow Gold'], ['Or Jaune', 'Yellow Gold'], ['Or jaune', 'Yellow Gold'],
    ['White Gold', 'White Gold'], ['Or Blanc', 'White Gold'], ['Or blanc', 'White Gold'],
    ['Rose Gold', 'Rose Gold'], ['Or Rose', 'Rose Gold'], ['Or rose', 'Rose Gold'],
    ['Gold', 'Gold'], ['Or', 'Gold'], ['Silver', 'Silver'], ['Argent', 'Silver'],
    ['Sterling Silver', 'Sterling Silver'], ['Argent Sterling', 'Sterling Silver'], ['Argent sterling', 'Sterling Silver'],
    ['Gold Vermeil', 'Gold Vermeil'], ['Vermeil', 'Gold Vermeil'], ['Platinum', 'Platinum'], ['Platine', 'Platinum'],
    ['925 Sterling Silver', '925 Sterling Silver'], ['Argent sterling 925', '925 Sterling Silver'],
    ['Argent Sterling 925', '925 Sterling Silver'], ['925 Argent sterling', '925 Sterling Silver'], ['925 Argent Sterling', '925 Sterling Silver']
  ]);
  const valueAliases = {
    Piece: new Map([
      ['Name necklace', 'Name necklace'], ['The Name Necklace', 'Name necklace'], ['Le collier prénom', 'Name necklace'],
      ['Coin necklace', 'Coin necklace'], ['The Coin Necklace', 'Coin necklace'], ['Le collier médaille', 'Coin necklace'],
      ['Bar necklace', 'Bar necklace'], ['The Bar Necklace', 'Bar necklace'], ['Le collier barre', 'Bar necklace']
    ]),
    Metal: materialAliases,
    Lettering: new Map([['Classic', 'Classic'], ['Classique', 'Classic'], ['Modern', 'Modern'], ['Moderne', 'Modern'], ['Signature', 'Signature']]),
    'Personalized order acknowledgement': new Map([['Yes', 'Yes'], ['Oui', 'Yes']])
  };
  const frenchValues = {
    Piece: new Map([['Name necklace', 'Le collier prénom'], ['Coin necklace', 'Le collier médaille'], ['Bar necklace', 'Le collier barre']]),
    Metal: new Map([
      ['Yellow Gold', 'Or Jaune'], ['White Gold', 'Or Blanc'], ['Rose Gold', 'Or Rose'], ['Gold', 'Or'], ['Silver', 'Argent'],
      ['Sterling Silver', 'Argent Sterling'], ['925 Sterling Silver', 'Argent Sterling 925'], ['Gold Vermeil', 'Vermeil'], ['Platinum', 'Platine']
    ]),
    Lettering: new Map([['Classic', 'Classique'], ['Modern', 'Moderne'], ['Signature', 'Signature']]),
    'Personalized order acknowledgement': new Map([['Yes', 'Oui']])
  };
  function canonicalMaterial(value) {
    if (typeof value !== 'string') return null;
    if (materialAliases.has(value)) return materialAliases.get(value);
    const match = /^(14K|18K) (.+)$/.exec(value);
    const metal = match && materialAliases.get(match[2]);
    return ['Yellow Gold', 'White Gold', 'Rose Gold', 'Gold'].includes(metal) ? match[1] + ' ' + metal : null;
  }
  function entries(properties) {
    if (properties == null) return [];
    let result;
    if (Array.isArray(properties)) {
      result = properties.map(entry => {
        if (!Array.isArray(entry) || entry.length !== 2 || typeof entry[0] !== 'string') fail('invalid property entry');
        return [entry[0], entry[1]];
      });
    } else {
      if (typeof properties !== 'object') fail('expected a property object');
      const prototype = Object.getPrototypeOf(properties);
      if (prototype !== Object.prototype && prototype !== null) fail('unexpected property object');
      result = [];
      for (const key of Reflect.ownKeys(properties)) {
        const descriptor = Object.getOwnPropertyDescriptor(properties, key);
        if (!descriptor.enumerable) continue;
        if (typeof key !== 'string' || !own(descriptor, 'value')) fail('unsupported property entry');
        result.push([key, descriptor.value]);
      }
    }
    const seen = new Set();
    for (const [key] of result) {
      if (seen.has(key)) fail('duplicate property key');
      seen.add(key);
    }
    return result;
  }
  function canonicalValue(key, value) {
    if (typeof value !== 'string') fail('expected text for ' + key);
    const aliases = own(valueAliases, key) ? valueAliases[key] : null;
    if (!aliases) return value;
    if (!aliases.has(value)) fail('unrecognized value for ' + key);
    return aliases.get(value);
  }
  function canonicalRecord(raw) {
    if (typeof raw !== 'string') fail('invalid canonical record');
    let parsed;
    try { parsed = JSON.parse(raw); } catch { fail('malformed canonical JSON'); }
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') fail('invalid canonical record');
    const pairs = Object.entries(parsed);
    if (!pairs.length) fail('empty canonical record');
    for (const [key, value] of pairs) {
      if (!fields.some(field => field[0] === key) || canonicalValue(key, value) !== value) fail('noncanonical record field');
    }
    const tokens = raw.match(/"(?:[^"\\]|\\.)*"/g) || [];
    if (tokens.length !== pairs.length * 2) fail('duplicate canonical field');
    const result = Object.create(null);
    for (const [key] of fields) { if (own(parsed, key)) result[key] = parsed[key]; }
    return result;
  }
  function read(properties) {
    const input = new Map(entries(properties)), visible = Object.create(null), unknown = Object.create(null);
    for (const [key, value] of input) {
      if (key === recordKey || key === versionKey) continue;
      const canonicalKey = labels.get(key);
      if (!canonicalKey) { unknown[key] = value; continue; }
      if (own(visible, canonicalKey)) fail('duplicate aliases for ' + canonicalKey);
      visible[canonicalKey] = canonicalValue(canonicalKey, value);
    }
    const hasRecord = input.has(recordKey);
    if (hasRecord !== input.has(versionKey)) fail('incomplete canonical metadata');
    let canonical = Object.create(null);
    if (hasRecord) {
      if (input.get(versionKey) !== '1') fail('unsupported canonical version');
      canonical = canonicalRecord(input.get(recordKey));
      for (const [key] of fields) {
        if (own(canonical, key) !== own(visible, key) || (own(canonical, key) && canonical[key] !== visible[key])) fail('canonical and visible values disagree for ' + key);
      }
    } else {
      for (const [key] of fields) { if (own(visible, key)) canonical[key] = visible[key]; }
    }
    return { canonical, unknown, migrated: hasRecord };
  }
  function localize(properties, locale) {
    if (typeof locale !== 'string' || !/^(en|fr)(?:-[A-Za-z0-9]+)*$/i.test(locale)) fail('unsupported locale');
    const french = locale.split('-')[0].toLowerCase() === 'fr';
    const { canonical, unknown } = read(properties);
    const result = Object.assign(Object.create(null), unknown);
    if (!Object.keys(canonical).length) return result;
    for (const [key, label] of fields) {
      if (!own(canonical, key)) continue;
      const values = french && own(frenchValues, key) ? frenchValues[key] : null;
      result[french ? label : key] = values ? values.get(canonical[key]) : canonical[key];
    }
    result[recordKey] = JSON.stringify(canonical);
    result[versionKey] = '1';
    return result;
  }
  window.MagdorCheckoutProperties = Object.freeze({ canonicalMaterial, read, localize });
  function syncRingSize(control) {
    const select = control.querySelector('[data-mp-ring-size]');
    const record = control.querySelector('[data-mp-ring-record]');
    if (select && record) record.value = JSON.stringify({ 'Ring size': select.value });
  }
  document.querySelectorAll('[data-mp-ring-size-control]').forEach(syncRingSize);
  document.addEventListener('change', event => {
    if (event.target.matches?.('[data-mp-ring-size]')) {
      const control = event.target.closest('[data-mp-ring-size-control]');
      if (control) syncRingSize(control);
    }
  });
  document.addEventListener('shopify:section:load', event => {
    event.target.querySelectorAll('[data-mp-ring-size-control]').forEach(syncRingSize);
  });
  document.dispatchEvent(new Event('magdor:checkout-properties-ready'));
})();
